import { Box, SxProps, Typography, useMediaQuery, useTheme } from '@mui/material';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import { formatCompanyAddress, getCompanyShortName, getDeliveryZoneFromAdministrativeArea, getMinimumOrderValueFromDeliveryZones } from '../../common/utils/common.utils';
import CompanyDescription from '../CompanyDescription';
import { ContractPreview } from '../../hooks/useContracts';
import SizedImage from '../../common/components/SizedImage';
import FileUtils from '../../common/utils/file.utils';
import SocialTagCard from '../../social-proof/SocialTagCard';
import useRelevantSocialTags from '../../social-proof/hooks/useRelevantSocialTags';
import InfoSocialTags from '../../social-proof/InfoSocialTags';
import { MinimumOrderValue } from '../../common/state/state';
import { currentContextAtom } from '../../state';
import WishlistSupplierButton from '../../wishlist/WishlistSupplierButton';
import ContactBox from './ContactBox';
import { CompanyInfo } from './CompanyInfoCard';
import CompanyServicesInfoLight from './CompanyServicesInfoLight';

export interface UnsignedSupplierInfoCardProps {
  company: CompanyInfo;
  contract?: ContractPreview;
  compact?: boolean;
  companyServicesSx?: SxProps;
  displayCompanyDescription?: boolean;
  displayCompanyAddress?: boolean;
  displaySocialTagCards?: boolean;
}

export default function UnsignedSupplierInfoCard({ company, contract, compact, companyServicesSx, displayCompanyDescription = true, displayCompanyAddress = true, displaySocialTagCards = true }: UnsignedSupplierInfoCardProps): React.JSX.Element {
  const { t } = useTranslation(['common']);
  const theme = useTheme();
  const isSmallerThanXl = useMediaQuery(theme.breakpoints.down('xl'));
  const imageWidth = isSmallerThanXl ? 445 : 622;
  const context = useAtomValue(currentContextAtom);

  const socialTags = useRelevantSocialTags(company.social_tags, { rules: { 1: 1, 2: 1 } });
  const targetGeoLoc = { lat: company.addresses[0].latitude, lng: company.addresses[0].longitude };

  const mov: MinimumOrderValue | undefined = useMemo(() => {
    const movFromDeliveryZone = getMinimumOrderValueFromDeliveryZones(company.delivery_zones, context.companyAddress.administrativeAreaLevel2 || '');

    if (contract && contract.minimum_order_value) {
      return {
        type: contract.minimum_order_value_type,
        value: contract.minimum_order_value,
        negotiable: movFromDeliveryZone?.negotiable,
      };
    }

    return movFromDeliveryZone;
  }, [company, contract, context]);

  const deliveryZone = useMemo(() => {
    return getDeliveryZoneFromAdministrativeArea(company.delivery_zones, context.companyAddress.administrativeAreaLevel2 || '');
  }, [company, context])

  if (compact) {
    return (
      <Box
        sx={{
          my: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        <Box>
          {displayCompanyAddress && (<Typography variant="bodyMedium" color="secondary" sx={{ mb: 1 }}>{formatCompanyAddress(company.addresses[0])}</Typography>)}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <InfoSocialTags
              isWholesaler={company.is_wholesaler}
              minimumOrderValue={mov}
              deliveryTime={deliveryZone?.delivery_time || undefined}
              targetGeoLoc={targetGeoLoc}
            />
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: {
              xs: 'column',
              sm: 'row',
            },
            gap: 1.5,
            '& > *': { flex: 1, minHeight: 117 },
          }}
        >
          {displayCompanyDescription && <CompanyDescription company={company} />}
          <CompanyServicesInfoLight sx={companyServicesSx} services={company} />
        </Box>
        {!!company.social_tags.length &&  displaySocialTagCards && (
          <Box
            sx={{
              display: 'flex',
              gap: {
                xs: 1.5,
                md: 2,
              },
              flexWrap: 'nowrap',
              '> *': {
                flex: 1,
                flexShrink: 0,
                flexBasis: 'auto',
              },
              overflowX: 'auto',
            }}
          >
            {socialTags.map((tag) => (
              <SocialTagCard key={tag.type} tag={tag} entity="SUPPLIER" />
            ))}
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        p: 1.5,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 3,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            alignItems: 'flex-start',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 2,
              width: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              <Typography
                variant="headlineLarge"
              >
                {getCompanyShortName(company.tradeName, company.business_profile)}
              </Typography>
              <Typography variant="bodyMedium" color="secondary">
                {formatCompanyAddress(company.addresses[0])}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
              <WishlistSupplierButton supplierId={company.id} />
              <ContactBox contactCompanyId={company.id} contract={contract} />
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
            }}
          >
            <InfoSocialTags
              isWholesaler={company.is_wholesaler}
              minimumOrderValue={mov}
              deliveryTime={deliveryZone?.delivery_time || undefined}
              targetGeoLoc={targetGeoLoc}
            />
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            '& > *': { flex: 1, minHeight: 117 },
          }}
        >
          <CompanyDescription company={company} />
          <CompanyServicesInfoLight services={company} />
        </Box>
        {!!company.social_tags.length && (
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              '> *': {
                flex: 1,
              },
            }}
          >
            {socialTags.map((tag) => (
              <SocialTagCard key={tag.type} tag={tag} entity="SUPPLIER" />
            ))}
          </Box>
        )}
      </Box>
      <SizedImage
        src={FileUtils.BuildCompanyImageUrl({
          photo: company.photos ? company.photos[0]?.photo : undefined,
          type: company.business_profile,
          size: { width: imageWidth },
        })}
        alt={t('common:company')}
        width={imageWidth}
        borderRadius={3}
        sx={{
          overflow: 'visible',
          '& img': {
            borderRadius: 3,
            aspectRatio: '16/9',
            objectFit: 'cover',
          },
        }}
      />
    </Box>
  );
}
